import { useEffect, useLayoutEffect, useState, useCallback, useRef } from 'react';
import type { ReactFlowInstance } from '@xyflow/react';

export interface TutorialStep {
  /** CSS selector for the element to spotlight. Empty = centered modal. */
  target?: string;
  /** Title of the tooltip. */
  title: string;
  /** Body of the tooltip. */
  body: string;
  /** Where the tooltip sits relative to the spotlight. */
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  /** Optional: zoom to a specific React Flow node ID. */
  zoomToNodeId?: string;
  /** Optional: highlight a different "scope" (drops the whole rest darker). */
  emphasizeCanvas?: boolean;
  /** Padding around the spotlight in px. */
  padding?: number;
  /** Optional inline "hint" caret/arrow direction. */
  arrow?: boolean;
}

interface Props {
  steps: TutorialStep[];
  open: boolean;
  onClose: (completed: boolean) => void;
  rfInstance?: ReactFlowInstance | null;
  paletteOpen?: boolean;
  propsOpen?: boolean;
  onRequestPalette?: (open: boolean) => void;
  onRequestProps?: (open: boolean) => void;
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const CENTERED: Rect = { top: 0, left: 0, width: 0, height: 0 };

function getRect(selector?: string, padding = 10): Rect | null {
  if (!selector) return null;
  const el = document.querySelector(selector) as HTMLElement | null;
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return {
    top: r.top - padding,
    left: r.left - padding,
    width: r.width + padding * 2,
    height: r.height + padding * 2,
  };
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

/**
 * Returns the desired sidebar state for a step.
 *  null  = leave as-is (don't touch)
 *  true  = open
 *  false = close
 */
function desiredMenuState(step: TutorialStep): { palette: boolean | null; props: boolean | null } {
  const t = (step.target || '').toLowerCase();

  // Targets inside the palette → palette must be open. Props doesn't matter; close it to focus.
  if (t.includes('palette') || t.includes('templates') || t.includes('starter')) {
    return { palette: true, props: false };
  }

  // Targets inside the props panel → props open. Palette closed to focus.
  if (t.includes('props')) {
    return { palette: false, props: true };
  }

  // Targeting a specific node on the canvas (zoomToNodeId set, or selector starts with [data-node-id])
  // or emphasizing the canvas → close both sidebars so the canvas is fully visible
  if (step.zoomToNodeId || step.emphasizeCanvas || t.includes('canvas') || t.includes('data-node-id') || t.includes('.fnode') || t.includes('.vnode')) {
    return { palette: false, props: false };
  }

  // Targets in the toolbar / FAB / test-run / save / active-toggle — sidebars don't need to be open.
  // Default: close both to give the user a clean view, except when no target (centered step).
  if (step.target) {
    return { palette: false, props: false };
  }

  // Centered welcome / completion step → leave whatever was open
  return { palette: null, props: null };
}

function rectsOverlap(a: Rect, b: Rect): boolean {
  return !(
    a.left + a.width <= b.left ||
    b.left + b.width <= a.left ||
    a.top + a.height <= b.top ||
    b.top + b.height <= a.top
  );
}

function overlapArea(a: Rect, b: Rect): number {
  const x = Math.max(0, Math.min(a.left + a.width, b.left + b.width) - Math.max(a.left, b.left));
  const y = Math.max(0, Math.min(a.top + a.height, b.top + b.height) - Math.max(a.top, b.top));
  return x * y;
}

/**
 * Place the tooltip alongside the spotlight, picking the side with the most free space
 * and never overlapping the spotlight rect. Centered fallback only when there's truly
 * no free real estate (in which case the algorithm picks the corner with most space).
 */
function computeTooltipPos(rect: Rect, placement: TutorialStep['placement'], ttW: number, ttH: number) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const gap = 18;
  const margin = 14;

  // Centered welcome step → center of viewport
  if (placement === 'center' || !rect.width) {
    return {
      top: clamp(vh / 2 - ttH / 2, margin, vh - ttH - margin),
      left: clamp(vw / 2 - ttW / 2, margin, vw - ttW - margin),
    };
  }

  // Available free space on each side of the spotlight rect
  const spaceTop    = rect.top - margin;
  const spaceBottom = vh - (rect.top + rect.height) - margin;
  const spaceLeft   = rect.left - margin;
  const spaceRight  = vw - (rect.left + rect.width) - margin;

  type Dir = 'top' | 'bottom' | 'left' | 'right';

  function spaceOn(dir: Dir): number {
    if (dir === 'top') return spaceTop;
    if (dir === 'bottom') return spaceBottom;
    if (dir === 'left') return spaceLeft;
    return spaceRight;
  }

  function placeOn(dir: Dir): { top: number; left: number } {
    let t = 0, l = 0;
    if (dir === 'bottom') {
      t = rect.top + rect.height + gap;
      l = rect.left + rect.width / 2 - ttW / 2;
    } else if (dir === 'top') {
      t = rect.top - ttH - gap;
      l = rect.left + rect.width / 2 - ttW / 2;
    } else if (dir === 'left') {
      t = rect.top + rect.height / 2 - ttH / 2;
      l = rect.left - ttW - gap;
    } else {
      t = rect.top + rect.height / 2 - ttH / 2;
      l = rect.left + rect.width + gap;
    }
    return { top: t, left: l };
  }

  // Build the ordered preference list: requested → biggest-space fallbacks
  const requested = (placement || 'right') as Dir;
  const others: Dir[] = (['bottom', 'right', 'top', 'left'] as Dir[]).filter(d => d !== requested);
  others.sort((a, b) => spaceOn(b) - spaceOn(a));
  const order: Dir[] = [requested, ...others];

  // Try each direction. Required: the chosen direction has enough space for tooltip
  // AND the placed tooltip doesn't overlap the spotlight after clamping.
  for (const dir of order) {
    const need = (dir === 'top' || dir === 'bottom') ? ttH + gap : ttW + gap;
    if (spaceOn(dir) < need) continue;
    let { top, left } = placeOn(dir);
    top = clamp(top, margin, vh - ttH - margin);
    left = clamp(left, margin, vw - ttW - margin);
    const ttRect: Rect = { top, left, width: ttW, height: ttH };
    if (!rectsOverlap(ttRect, rect)) {
      return { top, left };
    }
  }

  // Nothing fit cleanly — pick the corner with the LEAST overlap with the spotlight.
  // (Some overlap is allowed when the spotlight fills most of the viewport, but we
  // want to minimize it so as much of the target as possible stays visible.)
  const corners: Array<{ top: number; left: number }> = [
    { top: margin, left: margin },
    { top: margin, left: vw - ttW - margin },
    { top: vh - ttH - margin, left: margin },
    { top: vh - ttH - margin, left: vw - ttW - margin },
  ];

  let best = corners[0];
  let bestOverlap = Infinity;
  for (const c of corners) {
    const ttRect: Rect = { top: c.top, left: c.left, width: ttW, height: ttH };
    const ov = overlapArea(ttRect, rect);
    if (ov < bestOverlap) {
      bestOverlap = ov;
      best = c;
      if (ov === 0) break;
    }
  }
  return { top: best.top, left: best.left };
}

export function Tutorial({ steps, open, onClose, rfInstance, paletteOpen, propsOpen, onRequestPalette, onRequestProps }: Props) {
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState<Rect>(CENTERED);
  // Cap tooltip width at 320px (was 360) so it fits more cleanly next to spotlights.
  // Also shrink further on narrow viewports.
  const initialTtW = (() => {
    const vw = window.innerWidth;
    if (vw < 640) return Math.min(300, vw - 28);
    if (vw < 900) return Math.min(310, vw - 60);
    return 320;
  })();
  const [ttSize, setTtSize] = useState({
    w: initialTtW,
    h: 200,
  });
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });

  const step = steps[idx];

  // Reset on open
  useEffect(() => {
    if (open) setIdx(0);
  }, [open]);

  // Stash initial menu state once the tutorial starts so we can restore on close
  const initialPaletteRef = useRef<boolean | null>(null);
  const initialPropsRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (open && initialPaletteRef.current === null) {
      initialPaletteRef.current = !!paletteOpen;
      initialPropsRef.current = !!propsOpen;
    } else if (!open) {
      // Restore prior state when tutorial closes
      if (initialPaletteRef.current !== null && onRequestPalette) {
        onRequestPalette(initialPaletteRef.current);
      }
      if (initialPropsRef.current !== null && onRequestProps) {
        onRequestProps(initialPropsRef.current);
      }
      initialPaletteRef.current = null;
      initialPropsRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Decide which sidebars each step needs and open/close them accordingly.
  useEffect(() => {
    if (!open || !step) return;
    const want = desiredMenuState(step);
    if (want.palette !== null && want.palette !== paletteOpen && onRequestPalette) {
      onRequestPalette(want.palette);
    }
    if (want.props !== null && want.props !== propsOpen && onRequestProps) {
      onRequestProps(want.props);
    }
  }, [open, step, paletteOpen, propsOpen, onRequestPalette, onRequestProps]);

  // Zoom canvas to specific node when requested
  useEffect(() => {
    if (!open || !step || !rfInstance) return;
    if (step.zoomToNodeId) {
      try {
        const nodes = rfInstance.getNodes();
        const target = nodes.find(n => n.id === step.zoomToNodeId);
        if (target) {
          // Lower maxZoom + higher padding so the node never fills the viewport.
          // Leaves >40% breathing room on each axis for the tooltip.
          rfInstance.fitView({ nodes: [target], duration: 600, padding: 0.9, maxZoom: 0.95 });
        }
      } catch { /* noop */ }
    } else if (step.emphasizeCanvas) {
      try {
        rfInstance.fitView({ duration: 600, padding: 0.3, maxZoom: 1.0 });
      } catch { /* noop */ }
    }
  }, [open, step, rfInstance]);

  // Recompute spotlight rect when step changes / window resizes / canvas moves.
  // If the target isn't in the DOM yet (e.g. menu still animating open), keep trying.
  const recompute = useCallback(() => {
    if (!step) return;
    const r = getRect(step.target, step.padding);
    // Check if target is offscreen — if so, scroll it into view first
    if (r && (r.top < 0 || r.left < 0 || r.top + r.height > window.innerHeight || r.left + r.width > window.innerWidth)) {
      const el = document.querySelector(step.target!);
      if (el) {
        try { el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' }); } catch { /* noop */ }
      }
    }
    setRect(r || CENTERED);
  }, [step]);

  useLayoutEffect(() => {
    if (!open) return;
    // Delay multiple times to let menu open animations + zoom animations settle
    const timers = [50, 200, 400, 700, 1000].map(t => window.setTimeout(recompute, t));
    return () => { timers.forEach(clearTimeout); };
  }, [open, idx, recompute, paletteOpen, propsOpen]);

  useEffect(() => {
    if (!open) return;
    const handler = () => recompute();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, [open, recompute]);

  // Compute tooltip position based on rect + measured size
  useEffect(() => {
    if (!step) return;
    setTooltipPos(computeTooltipPos(rect, step.placement, ttSize.w, ttSize.h));
  }, [rect, step, ttSize]);

  // Keyboard nav
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose(false);
      if (e.key === 'ArrowRight' || e.key === 'Enter') next();
      if (e.key === 'ArrowLeft') prev();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (!open || !step) return null;

  const isCentered = step.placement === 'center' || !rect.width;
  const total = steps.length;

  function next() {
    if (idx >= total - 1) onClose(true);
    else setIdx(i => i + 1);
  }
  function prev() {
    if (idx > 0) setIdx(i => i - 1);
  }

  return (
    <div className="tutorial-root" role="dialog" aria-modal="true">
      {/* Full backdrop with cutout for spotlight using a positioned div + box-shadow trick */}
      {!isCentered && (
        <div
          className="tutorial-spotlight"
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          }}
        />
      )}
      {isCentered && <div className="tutorial-backdrop" />}

      <TooltipBody
        step={step}
        idx={idx}
        total={total}
        tooltipPos={tooltipPos}
        width={ttSize.w}
        onMeasure={h => {
          if (Math.abs(h - ttSize.h) > 8) setTtSize(s => ({ ...s, h }));
        }}
        onSkip={() => onClose(false)}
        onNext={next}
        onPrev={prev}
      />
    </div>
  );
}

interface TooltipBodyProps {
  step: TutorialStep;
  idx: number;
  total: number;
  tooltipPos: { top: number; left: number };
  width: number;
  onMeasure: (height: number) => void;
  onSkip: () => void;
  onNext: () => void;
  onPrev: () => void;
}

function TooltipBody({ step, idx, total, tooltipPos, width, onMeasure, onSkip, onNext, onPrev }: TooltipBodyProps) {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!ref.current) return;
    const h = ref.current.getBoundingClientRect().height;
    onMeasure(h);
    // Re-measure only when content changes (step/title/body change)
    // No deps on tooltipPos to avoid infinite loop
  }, [step.title, step.body, step.target, idx, onMeasure]);

  return (
    <div
      ref={ref}
      className={`tutorial-tooltip ${step.arrow ? 'with-arrow' : ''}`}
      style={{ top: tooltipPos.top, left: tooltipPos.left, width }}
    >
      <div className="tt-head">
        <span className="tt-step">Step {idx + 1} of {total}</span>
        <button className="tt-skip" onClick={onSkip} aria-label="Skip tutorial">Skip</button>
      </div>
      <h3>{step.title}</h3>
      <p>{step.body}</p>
      <div className="tt-progress">
        {Array.from({ length: total }).map((_, i) => (
          <span key={i} className={`tt-dot ${i === idx ? 'on' : ''} ${i < idx ? 'done' : ''}`} />
        ))}
      </div>
      <div className="tt-actions">
        <button className="tt-btn ghost" disabled={idx === 0} onClick={onPrev}>← Back</button>
        <button className="tt-btn primary" onClick={onNext}>
          {idx === total - 1 ? 'Finish' : 'Next →'}
        </button>
      </div>
    </div>
  );
}
